import { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';

import CrmDashboardView from '#crmFeature/components/dashboard/CrmDashboardView/CrmDashboardView';
import HotDealsList from '#crmFeature/components/dashboard/HotDealsList/HotDealsList';
import OnboardingModal from '#crmFeature/components/onboarding/OnboardingModal/OnboardingModal';
import Page from '#globalComponents/Page/Page';
import PageHeader from '#globalComponents/PageHeader/PageHeader';
import { useCapabilityCheck } from '#globalHooks/useCapabilityCheck';
import { useSaveUserSettings } from '#settingsFeature/models/userSettings/mutations';
import { useUserSettings } from '#settingsFeature/models/userSettings/queries';

import GettingStartedCard from '../../components/DashboardPage/GettingStartedCard/GettingStartedCard';
import QuickActionsCard from '../../components/DashboardPage/QuickActionsCard/QuickActionsCard';
import RecentSearchesCard from '../../components/DashboardPage/RecentSearchesCard/RecentSearchesCard';
import StrategyAssistantCard from '../../components/DashboardPage/StrategyAssistantCard/StrategyAssistantCard';

// CRM users reach the strategy assistant via the CRM; outside production it stays reachable for testing.
const SHOW_STRATEGY_FOR_CRM = process.env.REACT_APP_NODE_ENV !== 'production';

/**
 * Unified dashboard.
 *
 * - Every new user sees the first-run onboarding modal (Vertriebsstrategie +
 *   leads, no pipeline) until they complete or dismiss it. "Seen" is persisted
 *   in userSettings.onboardingCompletedAt, so it does not depend on CRM access.
 * - Users with `crm.access` see the KI-Vertriebsleiter (daily plan, todos, hot
 *   deals); others see the getting-started checklist.
 */

const DashboardPage = () => {
	const navigate = useNavigate();
	const { hasCapability: canUseCrm, isLoading } = useCapabilityCheck('crm.access');
	const { data: settings, isLoading: settingsLoading } = useUserSettings();
	const saveUserSettings = useSaveUserSettings();

	const [showOnboarding, setShowOnboarding] = useState(false);

	const hasOnboarded = Boolean(settings?.userSettings?.onboardingCompletedAt);

	// Auto-open the first-run onboarding once for users who have not seen it yet
	useEffect(() => {
		if (settingsLoading || isLoading) return;
		if (!hasOnboarded) {
			setShowOnboarding(true);
		}
	}, [settingsLoading, isLoading, hasOnboarded]);

	// Persist that onboarding has been seen, then close. Merge into the existing
	// settings bag — the PATCH replaces the whole userSettings object.
	const handleCloseOnboarding = () => {
		setShowOnboarding(false);
		if (!hasOnboarded) {
			saveUserSettings.mutate({
				updates: {
					userSettings: {
						...(settings?.userSettings ?? {}),
						onboardingCompletedAt: new Date().toISOString(),
					},
				},
			});
		}
	};

	const openCompany = (companyId) => navigate(`/crm/companies/${companyId}`);
	const showStrategyAssistant = !canUseCrm || SHOW_STRATEGY_FOR_CRM;

	const header = (
		<PageHeader
			subtitle={canUseCrm ? 'Ihr Tagesplan und die heißesten Deals' : 'Ihr Einstieg in die Lead-Generierung'}
		/>
	);

	if (isLoading) {
		return (
			<Page header={header}>
				<p className="text-sm text-bgBase-800">Dashboard wird geladen …</p>
			</Page>
		);
	}

	return (
		<Page header={header}>
			<div className="grid grid-cols-1 lg:grid-cols-3 gap-5 items-start">
				<div className="lg:col-span-2 space-y-5">
					{showStrategyAssistant && <StrategyAssistantCard onStart={() => setShowOnboarding(true)} />}
					{canUseCrm ? (
						<CrmDashboardView onOpenCompany={openCompany} />
					) : (
						<>
							<GettingStartedCard />
							<RecentSearchesCard />
						</>
					)}
				</div>
				<div className="space-y-5">
					{canUseCrm && (
						<>
							<HotDealsList onOpenCompany={openCompany} />
							<RecentSearchesCard />
						</>
					)}
					<QuickActionsCard />
				</div>
			</div>

			<OnboardingModal isOpen={showOnboarding} onClose={handleCloseOnboarding} />
		</Page>
	);
};

export default DashboardPage;
